import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Ambulance, LogOut, Wifi, WifiOff, MapPin, Phone, Clock, AlertTriangle } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STADIA_KEY = process.env.REACT_APP_STADIA_API_KEY;
const STADIA_TILE_URL = `https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png?api_key=${STADIA_KEY}`;

const redIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

function useWebSocket(onMessage) {
  const [connected, setConnected] = useState(false);

  const connect = useCallback(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://") + "/api/ws";
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => setConnected(true);
    ws.onclose = () => {
      setConnected(false);
      setTimeout(connect, 3000);
    };
    ws.onerror = () => ws.close();
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        onMessage(data);
      } catch {}
    };

    return ws;
  }, [onMessage]);

  useEffect(() => {
    const ws = connect();
    return () => ws.close();
  }, [connect]);

  return connected;
}

export default function AmbulanceConsole({ user, onLogout }) {
  const navigate = useNavigate();
  const [selectedUnit, setSelectedUnit] = useState(user.unitId || null);
  const [selectedUnitName, setSelectedUnitName] = useState(user.unitName || null);
  const [units, setUnits] = useState([]);
  const [currentJob, setCurrentJob] = useState(null);
  const [unitStatus, setUnitStatus] = useState("available");
  const [updating, setUpdating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const unitsRes = await axios.get(`${API}/units`);
      setUnits(unitsRes.data);

      if (selectedUnit) {
        const unit = unitsRes.data.find((u) => u.id === selectedUnit);
        if (unit) {
          setUnitStatus(unit.status);
          if (unit.current_job_id) {
            const jobRes = await axios.get(`${API}/jobs/${unit.current_job_id}`);
            setCurrentJob(jobRes.data);
          } else {
            setCurrentJob(null);
          }
        }
      }
    } catch {}
  }, [selectedUnit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleWsMessage = useCallback((msg) => {
    if (msg.type === "unit_updated" && msg.unit?.id === selectedUnit) {
      setUnitStatus(msg.unit.status);
      if (!msg.unit.current_job_id) {
        setCurrentJob(null);
      }
    }
    if (msg.type === "job_created" && msg.job?.assigned_unit_id === selectedUnit) {
      setCurrentJob(msg.job);
      setUnitStatus("dispatched");
    }
    if (msg.type === "job_updated") {
      if (msg.job?.assigned_unit_id === selectedUnit) {
        setCurrentJob(msg.job);
      } else if (currentJob && msg.job?.id === currentJob.id && msg.job?.assigned_unit_id !== selectedUnit) {
        setCurrentJob(null);
      }
    }
    // Update units list
    if (msg.type === "unit_updated" || msg.type === "unit_created") {
      setUnits((prev) => {
        const exists = prev.find((u) => u.id === msg.unit.id);
        if (exists) return prev.map((u) => (u.id === msg.unit.id ? msg.unit : u));
        return [...prev, msg.unit];
      });
    }
  }, [selectedUnit, currentJob]);

  const wsConnected = useWebSocket(handleWsMessage);

  const handleStatusUpdate = async (newStatus) => {
    if (!selectedUnit || updating) return;
    setUpdating(true);
    try {
      await axios.put(`${API}/units/${selectedUnit}/status`, { status: newStatus });
      setUnitStatus(newStatus);
      if (newStatus === "available") {
        setCurrentJob(null);
      }
    } catch (err) {
      console.error("Failed to update status:", err);
    }
    setUpdating(false);
  };

  const handleLogout = () => {
    onLogout();
    navigate("/");
  };

  const handleSelectUnit = (unit) => {
    setSelectedUnit(unit.id);
    setSelectedUnitName(unit.name);
    setUnitStatus(unit.status);
  };

  const statusButtons = [
    { status: "en_route", label: "En Route" },
    { status: "on_scene", label: "On Scene" },
    { status: "transporting", label: "Transporting" },
    { status: "available", label: "Available" },
  ];

  const priorityLabel = (p) => {
    if (p === 1) return "PRIORITY 1 - CRITICAL";
    if (p === 2) return "PRIORITY 2 - EMERGENCY";
    if (p === 3) return "PRIORITY 3 - URGENT";
    if (p === 4) return "PRIORITY 4 - NON-URGENT";
    return `PRIORITY ${p}`;
  };

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Top Nav */}
      <div className="top-nav">
        <div className="nav-left">
          <span className="nav-brand">
            <Ambulance size={14} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
            {selectedUnitName || "Volunteer EMS"} - Ambulance Console
          </span>
        </div>
        <div className="nav-right">
          <span>{user.username} / {user.agency}</span>
          <span data-testid="ws-status-ambulance">
            {wsConnected ? <Wifi size={12} color="#00CC66" /> : <WifiOff size={12} color="#FF3333" />}
          </span>
          <span className={`ws-indicator ${wsConnected ? "connected" : "disconnected"}`} />
          <button data-testid="ambulance-logout-btn" className="logout-btn" onClick={handleLogout}>
            <LogOut size={11} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
            Logout
          </button>
        </div>
      </div>

      {/* Unit Selection (if not selected yet) */}
      {!selectedUnit ? (
        <div className="ambulance-container">
          <div className="unit-select-container">
            <Ambulance size={48} color="#00BFFF" />
            <h3>Select Your Unit</h3>
            <div className="unit-select-grid">
              {units
                .filter((u) => !user.agency || u.agency === user.agency)
                .map((u) => (
                  <button
                    key={u.id}
                    data-testid={`select-unit-${u.name}`}
                    className="unit-select-btn"
                    onClick={() => handleSelectUnit(u)}
                  >
                    {u.name}
                  </button>
                ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="ambulance-container">
          {/* Current Status */}
          <div className="section-box">
            <div className="section-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Current Status</span>
              <span
                className={`status-${unitStatus}`}
                style={{ textTransform: "uppercase", fontWeight: 700 }}
                data-testid="current-unit-status"
              >
                {unitStatus.replace("_", " ")}
              </span>
            </div>
          </div>

          {/* Current Assignment */}
          <div className="ambulance-assignment">
            <div className="section-header">
              {currentJob ? `Active Assignment - CAD #${currentJob.cad_number}` : "No Active Assignment"}
            </div>
            {currentJob ? (
              <div className="assignment-detail" data-testid="current-assignment">
                <div className="detail-item" style={{ gridColumn: "1 / -1" }}>
                  <span className="detail-label">Call Type</span>
                  <span className="detail-value" style={{ fontSize: 22 }}>{currentJob.call_type}</span>
                </div>
                <div className="detail-item" style={{ gridColumn: "1 / -1" }}>
                  <span className="detail-label">
                    <MapPin size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
                    Location
                  </span>
                  <span className="detail-value" style={{ fontSize: 20 }}>
                    {currentJob.location}
                    {currentJob.apt ? `, APT ${currentJob.apt}` : ""}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">
                    <AlertTriangle size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
                    Priority
                  </span>
                  <span className={`detail-value priority-${currentJob.priority}`}>
                    {priorityLabel(currentJob.priority)}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">
                    <Phone size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
                    Phone
                  </span>
                  <span className="detail-value">{currentJob.phone || "N/A"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Caller Source</span>
                  <span className="detail-value">{currentJob.caller_source || "N/A"}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">
                    <Clock size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
                    Dispatched
                  </span>
                  <span className="detail-value" style={{ fontSize: 13 }}>
                    {new Date(currentJob.created_at).toLocaleTimeString()}
                  </span>
                </div>
                {currentJob.location_data?.length > 0 && (
                  <div className="detail-item" style={{ gridColumn: "1 / -1" }}>
                    <span className="detail-label">Location Notes</span>
                    {currentJob.location_data.map((note, i) => (
                      <span key={i} className="detail-value" style={{ fontSize: 13 }}>{note}</span>
                    ))}
                  </div>
                )}
                {currentJob.call_info?.length > 0 && (
                  <div className="detail-item" style={{ gridColumn: "1 / -1" }}>
                    <span className="detail-label">Call Notes</span>
                    {currentJob.call_info.map((note, i) => (
                      <span key={i} className="detail-value" style={{ fontSize: 13 }}>{note}</span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="no-assignment" data-testid="no-assignment">
                Waiting for dispatch...
              </div>
            )}
          </div>

          {/* Map View for Current Job */}
          {currentJob && currentJob.location && (
            <AmbulanceMap location={currentJob.location} apt={currentJob.apt} />
          )}

          {/* Status Update Buttons */}
          <div className="ambulance-status-grid" data-testid="status-buttons">
            {statusButtons.map((btn) => (
              <button
                key={btn.status}
                data-testid={`status-${btn.status}`}
                className={`status-update-btn ${btn.status} ${unitStatus === btn.status ? "active-status" : ""}`}
                onClick={() => handleStatusUpdate(btn.status)}
                disabled={updating}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════
   AMBULANCE MAP (Leaflet + Stadia Maps)
   ═══════════════════════════════════════════════ */
function AmbMapFlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => { if (center) map.flyTo(center, zoom, { duration: 1 }); }, [center, zoom, map]);
  return null;
}

function AmbulanceMap({ location, apt }) {
  const [coords, setCoords] = useState(null);
  const prevLocation = useRef(null);
  const defaultCenter = [40.6782, -73.9442];

  useEffect(() => {
    if (!location || location === prevLocation.current) return;
    prevLocation.current = location;
    const query = encodeURIComponent(`${location}, Brooklyn, NY`);
    fetch(`https://api.stadiamaps.com/geocoding/v1/search?text=${query}&api_key=${STADIA_KEY}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.features && data.features.length > 0) {
          const [lng, lat] = data.features[0].geometry.coordinates;
          setCoords([lat, lng]);
        }
      }).catch(() => {});
  }, [location]);

  const mapCenter = coords || defaultCenter;

  return (
    <div style={{ height: 200, border: "1px solid #333", background: "#000" }} data-testid="ambulance-map">
      <MapContainer center={mapCenter} zoom={coords ? 16 : 12} style={{ width: "100%", height: "100%" }} zoomControl={true} attributionControl={false}>
        <TileLayer url={STADIA_TILE_URL} />
        <AmbMapFlyTo center={mapCenter} zoom={coords ? 16 : 12} />
        {coords && (
          <Marker position={coords} icon={redIcon}>
            <Popup><strong>{location}</strong>{apt ? `, APT ${apt}` : ""}</Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
}
